import {
  BARS_URL_PREFIX,
  MANIFEST_URL,
  MAX_RISK_PCT,
  MIN_RISK_PCT,
  PLAY_BARS,
  TOTAL_WINDOW,
  WARMUP_BARS,
  Y_FLIP_PROBABILITY,
} from './config';
import {
  computePnl,
  computeRMultiple,
  computeShares,
  resolveBar,
  type ResolverOutcome,
} from './resolver';
import { computeStats } from './stats';
import * as storage from './storage';
import type {
  AccountState,
  AdvanceResult,
  Bar,
  Manifest,
  ManifestEntry,
  Session,
  Stats,
  StatsFilter,
  Trade,
  TradePlan,
} from './types';

let cachedManifest: Manifest | null = null;
let currentSession: Session | null = null;
let currentEntry: ManifestEntry | null = null;
let openTrade: Trade | null = null;
let cachedAccount: AccountState | null = null;
let recentClosed: Trade[] = []; // newest first; warmed by getRandomChart, prepended on close
let pendingWrites: Promise<void> = Promise.resolve();

function trackWrite(p: Promise<void>): void {
  pendingWrites = pendingWrites.then(() => p).catch((err) => {
    if (typeof console !== 'undefined') console.error('[tradingEngine] persistence error', err);
  });
}

async function loadManifest(): Promise<Manifest> {
  if (cachedManifest) return cachedManifest;
  const res = await fetch(MANIFEST_URL);
  if (!res.ok) throw new Error(`manifest fetch failed: ${res.status}`);
  cachedManifest = (await res.json()) as Manifest;
  return cachedManifest;
}

async function loadBars(file: string): Promise<Bar[]> {
  const url = BARS_URL_PREFIX + file;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`bars fetch failed: ${url} ${res.status}`);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const isGzip = bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
  let json: string;
  if (isGzip) {
    const ds = new DecompressionStream('gzip');
    const stream = new Blob([buf]).stream().pipeThrough(ds);
    json = await new Response(stream).text();
  } else {
    json = new TextDecoder().decode(buf);
  }
  const parsed = JSON.parse(json) as { bars: Bar[] };
  return parsed.bars;
}

function flipBars(bars: Bar[]): Bar[] {
  let min = Infinity;
  let max = -Infinity;
  for (const b of bars) {
    if (b.l < min) min = b.l;
    if (b.h > max) max = b.h;
  }
  const mid = (min + max) / 2;
  return bars.map((b) => ({
    o: 2 * mid - b.o,
    h: 2 * mid - b.l,
    l: 2 * mid - b.h,
    c: 2 * mid - b.c,
    v: b.v,
  }));
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'id-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function validatePlan(plan: TradePlan): void {
  if (!plan || typeof plan !== 'object') throw new Error('plan must be an object');
  if (plan.direction !== 'long' && plan.direction !== 'short') {
    throw new Error('plan.direction must be "long" or "short"');
  }
  if (typeof plan.entry !== 'number' || !Number.isFinite(plan.entry)) {
    throw new Error('plan.entry must be a finite number');
  }
  if (typeof plan.stop !== 'number' || !Number.isFinite(plan.stop)) {
    throw new Error('plan.stop must be a finite number');
  }
  if (typeof plan.target !== 'number' || !Number.isFinite(plan.target)) {
    throw new Error('plan.target must be a finite number');
  }
  if (plan.stop === plan.entry) {
    throw new Error('plan.stop must differ from plan.entry');
  }
  if (
    typeof plan.riskPct !== 'number' ||
    plan.riskPct < MIN_RISK_PCT ||
    plan.riskPct > MAX_RISK_PCT
  ) {
    throw new Error(
      `plan.riskPct must be between ${MIN_RISK_PCT} and ${MAX_RISK_PCT} (got ${plan.riskPct})`,
    );
  }
}

function applyResolutionToTrade(
  trade: Trade,
  outcome: Extract<ResolverOutcome, { kind: 'stop' | 'target' }>,
  exitBarIndex: number,
): Trade {
  const r = computeRMultiple(trade.plan.direction, trade.plan.entry, trade.plan.stop, outcome.exitPrice);
  const pnl = computePnl(trade.plan.direction, trade.plan.entry, outcome.exitPrice, trade.shares);
  return {
    ...trade,
    exitBarIndex,
    exitPrice: outcome.exitPrice,
    resolution: outcome.kind,
    rMultiple: r,
    pnl,
    exitedAt: Date.now(),
    equityAfter: trade.equityAtEntry + pnl,
  };
}

function applyManualExitToTrade(trade: Trade, exitPrice: number, exitBarIndex: number): Trade {
  const r = computeRMultiple(trade.plan.direction, trade.plan.entry, trade.plan.stop, exitPrice);
  const pnl = computePnl(trade.plan.direction, trade.plan.entry, exitPrice, trade.shares);
  return {
    ...trade,
    exitBarIndex,
    exitPrice,
    resolution: 'manual',
    rMultiple: r,
    pnl,
    exitedAt: Date.now(),
    equityAfter: trade.equityAtEntry + pnl,
  };
}

function commitClose(trade: Trade): void {
  if (cachedAccount && trade.pnl !== null) {
    const equity = cachedAccount.equity + trade.pnl;
    const peakEquity = Math.max(cachedAccount.peakEquity, equity);
    const currentDrawdown = peakEquity > 0 ? (peakEquity - equity) / peakEquity : 0;
    cachedAccount = { equity, peakEquity, currentDrawdown };
    const snapshot = cachedAccount;
    trackWrite(storage.saveAccount(snapshot));
  }
  recentClosed.unshift(trade);
  trackWrite(storage.updateTrade(trade));
  openTrade = null;
}

function tradeOrder(t: Trade): number {
  return t.exitedAt ?? t.enteredAt;
}

export async function getRandomChart(): Promise<Session> {
  const manifest = await loadManifest();
  const eligible = manifest.charts.filter((c) => c.barCount >= TOTAL_WINDOW);
  if (eligible.length === 0) {
    throw new Error(`no eligible charts in manifest (need >= ${TOTAL_WINDOW} bars)`);
  }
  const entry = eligible[Math.floor(Math.random() * eligible.length)];
  const allBars = await loadBars(entry.file);
  const maxStart = allBars.length - TOTAL_WINDOW;
  const start = Math.floor(Math.random() * (maxStart + 1));
  const window = allBars.slice(start, start + TOTAL_WINDOW);
  const yFlipped = Math.random() < Y_FLIP_PROBABILITY;
  const bars = yFlipped ? flipBars(window) : window;

  // Abandon any in-memory open trade — it stays in IndexedDB as 'open' so the
  // user's trade history is preserved, just unattached to any active session.
  openTrade = null;
  await pendingWrites;
  cachedAccount = await storage.getAccount();
  const allTrades = await storage.listTrades();
  recentClosed = allTrades
    .filter((t) => t.resolution !== 'open')
    .sort((a, b) => tradeOrder(b) - tradeOrder(a));

  const session: Session = {
    chartId: entry.id,
    fakeName: entry.fakeName,
    sector: entry.sector,
    bars,
    cursor: WARMUP_BARS,
    warmupBars: WARMUP_BARS,
    playBars: PLAY_BARS,
    yFlipped,
  };
  currentSession = session;
  currentEntry = entry;
  // Persist session metadata so a reload (or resumeOrCreateSession call) can
  // restore the same chart, window, flip state, and cursor.
  trackWrite(
    storage.setSessionMeta({ chartId: entry.id, start, yFlipped, cursor: WARMUP_BARS }),
  );
  return session;
}

/**
 * Restore the last persisted session if its underlying chart is still in the
 * manifest; otherwise fall back to picking a fresh random chart. Also restores
 * the in-memory open trade if one exists for the resumed chart.
 */
export async function resumeOrCreateSession(): Promise<Session> {
  const meta = await storage.getSessionMeta();
  if (!meta) return getRandomChart();

  const manifest = await loadManifest();
  const entry = manifest.charts.find((c) => c.id === meta.chartId);
  if (!entry) {
    // Stale chartId (data regenerated, manifest changed); start fresh.
    return getRandomChart();
  }

  const allBars = await loadBars(entry.file);
  if (meta.start < 0 || meta.start + TOTAL_WINDOW > allBars.length) {
    return getRandomChart();
  }
  if (meta.cursor < WARMUP_BARS || meta.cursor > TOTAL_WINDOW) {
    return getRandomChart();
  }

  const window = allBars.slice(meta.start, meta.start + TOTAL_WINDOW);
  const bars = meta.yFlipped ? flipBars(window) : window;

  await pendingWrites;
  cachedAccount = await storage.getAccount();
  const allTrades = await storage.listTrades();

  // Restore the open trade if one was left in flight on this chart. Multiple
  // open trades shouldn't happen under single-position semantics; pick the
  // most recently entered one as a defensive choice.
  const openOnThisChart = allTrades
    .filter((t) => t.chartId === entry.id && t.resolution === 'open')
    .sort((a, b) => b.enteredAt - a.enteredAt)[0];
  openTrade = openOnThisChart ?? null;

  recentClosed = allTrades
    .filter((t) => t.resolution !== 'open')
    .sort((a, b) => tradeOrder(b) - tradeOrder(a));

  const session: Session = {
    chartId: entry.id,
    fakeName: entry.fakeName,
    sector: entry.sector,
    bars,
    cursor: meta.cursor,
    warmupBars: WARMUP_BARS,
    playBars: PLAY_BARS,
    yFlipped: meta.yFlipped,
  };
  currentSession = session;
  currentEntry = entry;
  return session;
}

/** Returns the in-memory open trade (or null). Sync — for UI that needs to
 *  rehydrate its open-position panel after a session resume. */
export function getOpenTrade(): Trade | null {
  return openTrade;
}

export function submitTrade(plan: TradePlan): Trade {
  if (!currentSession || !currentEntry) {
    throw new Error('no active session — call getRandomChart() first');
  }
  if (openTrade) {
    throw new Error('an open trade already exists in this session — close it first');
  }
  validatePlan(plan);
  if (!cachedAccount) {
    throw new Error('account not loaded — call getRandomChart() first');
  }
  const equityAtEntry = cachedAccount.equity;
  const { shares, riskAmount } = computeShares(equityAtEntry, plan.riskPct, plan.entry, plan.stop);
  const trade: Trade = {
    id: makeId(),
    chartId: currentEntry.id,
    fakeName: currentEntry.fakeName,
    sector: currentEntry.sector,
    yFlipped: currentSession.yFlipped,
    plan,
    shares,
    riskAmount,
    equityAtEntry,
    entryBarIndex: currentSession.cursor,
    exitBarIndex: null,
    exitPrice: null,
    resolution: 'open',
    rMultiple: null,
    pnl: null,
    equityAfter: null,
    enteredAt: Date.now(),
    exitedAt: null,
  };
  openTrade = trade;
  trackWrite(storage.saveTrade(trade));
  return trade;
}

export function advanceBar(): AdvanceResult {
  if (!currentSession) throw new Error('no active session — call getRandomChart() first');
  // cursor is the index of the NEXT bar to reveal. When it equals bars.length we're done.
  if (currentSession.cursor >= currentSession.bars.length) {
    return { bar: null, resolved: null };
  }
  const idx = currentSession.cursor;
  const bar = currentSession.bars[idx];
  currentSession.cursor = idx + 1;
  // Persist the new cursor so a reload can resume on the next bar.
  persistCursor();
  if (!openTrade) {
    return { bar, resolved: null };
  }
  const outcome = resolveBar(
    openTrade.plan.direction,
    openTrade.plan.stop,
    openTrade.plan.target,
    bar,
  );
  if (outcome.kind === 'open') {
    return { bar, resolved: null };
  }
  const closed = applyResolutionToTrade(openTrade, outcome, idx);
  commitClose(closed);
  return { bar, resolved: closed };
}

function persistCursor(): void {
  if (!currentSession || !currentEntry) return;
  // Pull through the existing chartId/start/yFlipped from the live session;
  // we only need to update cursor. setSessionMeta rewrites the whole record
  // (single small object — cheaper than read-modify-write).
  // The session was created from a known start offset that we keep on the
  // session instance; reconstruct from the data we have on hand.
  trackWrite((async () => {
    const meta = await storage.getSessionMeta();
    if (!meta) return;
    if (meta.chartId !== currentEntry?.id) return; // session changed under us
    await storage.setSessionMeta({
      chartId: meta.chartId,
      start: meta.start,
      yFlipped: meta.yFlipped,
      cursor: currentSession ? currentSession.cursor : meta.cursor,
    });
  })());
}

export function closePosition(): Trade | null {
  if (!currentSession) throw new Error('no active session');
  if (!openTrade) return null;
  // Exit at the most recently revealed bar's close (cursor-1, since cursor points at next-to-reveal).
  // Before any advance, cursor = WARMUP_BARS, so this is the last warmup bar's close.
  const exitIdx = Math.max(0, currentSession.cursor - 1);
  const exitPrice = currentSession.bars[exitIdx].c;
  const closed = applyManualExitToTrade(openTrade, exitPrice, exitIdx);
  commitClose(closed);
  return closed;
}

/**
 * Returns all bars revealed so far this session: the warmup bars plus every
 * bar advanceBar has produced. Empty array if no session is active.
 *
 * Snapshot — safe to mutate or hand to a charting library.
 */
export function getVisibleBars(): Bar[] {
  if (!currentSession) return [];
  return currentSession.bars.slice(0, currentSession.cursor);
}

/**
 * Close of the most recently revealed bar — i.e. "now" from the user's POV.
 * Throws if no session is active.
 */
export function getCurrentPrice(): number {
  if (!currentSession) throw new Error('no active session');
  const idx = Math.max(0, currentSession.cursor - 1);
  return currentSession.bars[idx].c;
}

/**
 * Last `n` closed trades, newest first. Reads from an in-memory cache that's
 * warmed by getRandomChart() and updated whenever a trade closes — so this
 * is sync and safe to call inside render loops.
 */
export function getRecentTrades(n: number): Trade[] {
  if (n <= 0) return [];
  return recentClosed.slice(0, n);
}

export async function getStats(filter?: StatsFilter): Promise<Stats> {
  await pendingWrites;
  const trades = await storage.listTrades(filter);
  return computeStats(trades);
}

export async function getClosedTrades(filter?: StatsFilter): Promise<Trade[]> {
  await pendingWrites;
  const trades = await storage.listTrades(filter);
  return trades.filter((t) => t.resolution !== 'open');
}

export async function getAccount(): Promise<AccountState> {
  await pendingWrites;
  return storage.getAccount();
}

/** Test-only: clears in-memory engine state (does NOT touch storage). */
export function resetEngineForTests(): void {
  cachedManifest = null;
  currentSession = null;
  currentEntry = null;
  openTrade = null;
  cachedAccount = null;
  recentClosed = [];
  pendingWrites = Promise.resolve();
}

declare global {
  interface Window {
    tradingEngine?: {
      getRandomChart: typeof getRandomChart;
      resumeOrCreateSession: typeof resumeOrCreateSession;
      advanceBar: typeof advanceBar;
      submitTrade: typeof submitTrade;
      closePosition: typeof closePosition;
      getStats: typeof getStats;
      getClosedTrades: typeof getClosedTrades;
      getAccount: typeof getAccount;
      getVisibleBars: typeof getVisibleBars;
      getCurrentPrice: typeof getCurrentPrice;
      getRecentTrades: typeof getRecentTrades;
      getOpenTrade: typeof getOpenTrade;
    };
  }
}

if (typeof window !== 'undefined') {
  window.tradingEngine = {
    getRandomChart,
    resumeOrCreateSession,
    advanceBar,
    submitTrade,
    closePosition,
    getStats,
    getClosedTrades,
    getAccount,
    getVisibleBars,
    getCurrentPrice,
    getRecentTrades,
    getOpenTrade,
  };
}
